import { useState, useEffect } from "react";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";
import Grid from "@mui/material/Grid";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

import MDBox from "../../../components/MDBox";
import MDModal from "../../../components/MDModal";
import MDTypography from "../../../components/MDTypography";
import MDButton from "../../../components/MDButton";

import moment from "moment";

import {
  postCommentary,
  getCommentary,
  putCommentary,
  deleteCommentary,
} from "../../../api/ordercoms";

import OrderCommentaries from "./OrderCommentaries";

import { areaColor } from "../../../config/enum";

import PropTypes from "prop-types";

import { useSelector } from "react-redux";

export default function CreateEditOrderCommentaries({
  edit = false,
  commentaries,
  setCommentaries,
  order,
}) {
  const [open, setOpen] = useState(false);

  const [step, setStep] = useState(0);
  const steps = edit ? commentaries.map((_, i) => `commentaire ${i + 1}`) : ["commentaire 1"];
  const isLastStep = step === steps.length - 1;
  const isFirstStep = step === 0;

  const [commentaryId, setCommentaryId] = useState("");
  const [commentaryUserId, setCommentaryUserId] = useState(null);
  const [commentaryDate, setCommentaryDate] = useState(moment(new Date()).format("YYYY-MM-DD"));
  const [commentaryAction, setCommentaryAction] = useState("");

  const currentOrder = useSelector((state) => state.orders.currentOrder);

  useEffect(() => {
    if (edit && commentaries && commentaries[step] !== undefined) {
      setCommentaryId(commentaries[step].commentary_id);
      setCommentaryUserId({
        label: commentaries[step].commentary_user_name,
        id: commentaries[step].commentary_user_id,
      });
      setCommentaryDate(commentaries[step].commentary_date);
      setCommentaryAction(commentaries[step].commentary_action);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, commentaries]);

  async function createCommentaryAPI() {
    const commentary = {
      order_id: order.order_id,
      commentary_user_id: commentaryUserId ? commentaryUserId.id : null,
      commentary_date: commentaryDate && moment(commentaryDate).format("YYYY-MM-DD"),
      commentary_action: commentaryAction,
    };
    await postCommentary(commentary);
  }

  async function deleteCommentaryAPI() {
    await deleteCommentary(commentaryId);
  }

  async function updateCommentaryAPI() {
    const commentary = {
      order_id: order.order_id,
      commentary_user_id: commentaryUserId ? commentaryUserId.id : null,
      commentary_date: commentaryDate ? moment(commentaryDate).format("YYYY-MM-DD") : null,
      commentary_action: commentaryAction,
    };
    await putCommentary(commentary, commentaryId);
  }

  async function fetchCommentaries() {
    const response = await getCommentary(order.order_id);
    setCommentaries([
      ...new Map(response.data.map((item) => [item["commentary_id"], item])).values(),
    ]);
  }

  useEffect(() => {
    if (order) {
      fetchCommentaries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  function resetData() {
    setCommentaryAction("");
    setCommentaryDate(moment(new Date()).format("YYYY-MM-DD"));
    setCommentaryUserId(null);
  }

  async function deleteCom() {
    await deleteCommentaryAPI();
    await fetchCommentaries();
    exit();
  }

  async function cancel() {
    if (edit) {
      await fetchCommentaries();
    } else {
      resetData();
    }
    exit();
  }

  async function submit() {
    if (edit) {
      await updateCommentaryAPI();
      await fetchCommentaries();
    } else {
      await createCommentaryAPI();
      await fetchCommentaries();
      resetData();
    }
    exit();
  }

  function exit() {
    setStep(0);
    setOpen(false);
  }

  const next = () => setStep(step + 1);
  const previous = () => setStep(step - 1);

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  return (
    <>
      {edit ? (
        <IconButton size="medium" disableRipple color="inherit" onClick={() => setOpen(true)}>
          <Icon style={{ color: "#FFF" }}>edit</Icon>
        </IconButton>
      ) : (
        <IconButton size="medium" disableRipple color="inherit" onClick={() => setOpen(true)}>
          <Icon style={{ color: "#FFF" }}>add</Icon>
        </IconButton>
      )}
      <MDModal open={open} onClose={() => setOpen(false)}>
        <MDBox mt={25} mb={9}>
          <Grid container justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox
                  mt={edit ? -12 : -5}
                  mb={2}
                  mx={2}
                  variant="gradient"
                  sx={{
                    backgroundColor: currentOrder && areaColor[currentOrder.area],
                    boxShadow: `0 1px 6px 1px ${currentOrder && areaColor[currentOrder.area]}`,
                  }}
                  borderRadius="lg"
                  textAlign="center"
                >
                  <MDBox mt={2} mb={1}>
                    <MDBox>
                      {edit ? (
                        <MDTypography variant="h3" fontWeight="bold" style={{ color: "#FFF" }}>
                          Modification du commentaire
                        </MDTypography>
                      ) : (
                        <MDTypography variant="h3" fontWeight="bold" style={{ color: "#FFF" }}>
                          Ajouter un nouveau commentaire
                        </MDTypography>
                      )}
                    </MDBox>
                    <MDTypography variant="h5" fontWeight="regular" color="light">
                      Veuillez rentrer les informations relatives au commentaire
                    </MDTypography>
                  </MDBox>
                  {edit && (
                    <Stepper
                      sx={{ boxShadow: "none", backgroundImage: "none" }}
                      activeStep={step}
                      alternativeLabel
                    >
                      {steps.map((label) => (
                        <Step
                          sx={{
                            "& .MuiStepConnector-root.MuiStepConnector-horizontal.MuiStepConnector-alternativeLabel.Mui-active":
                              {
                                color: darkMode && "#344767 !important",
                              },
                            "& .MuiStepConnector-root.MuiStepConnector-horizontal.MuiStepConnector-alternativeLabel.Mui-completed":
                              {
                                color: darkMode && "#344767 !important",
                              },
                          }}
                          key={label}
                        >
                          <StepLabel
                            sx={{
                              "& .MuiStepLabel-label.Mui-active": {
                                color: darkMode && "#344767 !important",
                              },
                              "& .MuiStepLabel-label.Mui-completed": {
                                color: darkMode && "#344767 !important",
                              },
                              "& .MuiStepIcon-root.Mui-active": {
                                background: darkMode && "#344767",
                                fill: darkMode && "#344767",
                                stroke: darkMode && "#344767",
                                borderColor: darkMode && "#344767",
                                boxShadow: darkMode && "0rem 0rem 0rem 0.125rem #344767",
                              },
                              "& .MuiStepIcon-root.Mui-completed": {
                                background: darkMode && "#344767",
                                fill: darkMode && "#344767",
                                stroke: darkMode && "#344767",
                                borderColor: darkMode && "#344767",
                                boxShadow: darkMode && "0rem 0rem 0rem 0.125rem #344767",
                              },
                            }}
                          >
                            {label}
                          </StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  )}
                </MDBox>
                <MDBox p={2}>
                  <MDBox>
                    <OrderCommentaries
                      commentaryId={commentaryId}
                      commentaryUserId={commentaryUserId}
                      setCommentaryUserId={setCommentaryUserId}
                      commentaryDate={commentaryDate}
                      setCommentaryDate={setCommentaryDate}
                      commentaryAction={commentaryAction}
                      setCommentaryAction={setCommentaryAction}
                    />
                    <MDBox mt={3} width="100%" display="flex" justifyContent="space-between">
                      <MDBox width="50%">
                        {isFirstStep ? (
                          <MDBox />
                        ) : (
                          <MDButton variant="gradient" color="warning" onClick={previous}>
                            Précédent
                          </MDButton>
                        )}
                      </MDBox>
                      <MDBox textAlign="right" width="50%">
                        <Grid container justifyContent="right" spacing={1.5}>
                          {isLastStep && steps.length > 0 ? (
                            <MDBox />
                          ) : (
                            <Grid item>
                              <MDButton variant="gradient" color="info" onClick={next}>
                                Suivant
                              </MDButton>
                            </Grid>
                          )}
                          <Grid item>
                            <MDButton variant="gradient" color="warning" onClick={cancel}>
                              Annuler
                            </MDButton>
                          </Grid>
                          <Grid item>
                            <MDButton
                              disabled={commentaryUserId === null}
                              variant="gradient"
                              color="success"
                              onClick={submit}
                            >
                              Envoyer
                            </MDButton>
                          </Grid>
                          {edit && (
                            <Grid item>
                              <MDButton variant="gradient" color="error" onClick={deleteCom}>
                                Supprimer le commentaire
                              </MDButton>
                            </Grid>
                          )}
                        </Grid>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDModal>
    </>
  );
}

CreateEditOrderCommentaries.propTypes = {
  edit: PropTypes.bool.isRequired,
};
